"use client";

import { useEffect, useRef, useState } from "react";
import { LinkMark } from "./brand-marks.tsx";
import { CheckIcon } from "./icons.tsx";
import { copyText } from "../shared/clipboard.ts";
import { parseNote, type NoteToken } from "../shared/note-parse.ts";

/**
 * A note, drawn.
 *
 * Every link is a button with a mark, whether it was picked from the `#` menu,
 * produced by a shortcut, or pasted in the middle of a sentence, because all
 * three are the same thing once written and the icon is derived from the URL
 * rather than from how it got there.
 *
 * A link the reader cannot open in Drive still shows, labelled. Hiding it would
 * make the note read as though it never mentioned the sheet, which is worse than
 * saying plainly that there is one and it is not theirs: the second is a thing
 * they can ask about.
 */
export default function NoteBody({
  body,
  access,
  me,
  people,
}: {
  body: string;
  /** url → can this reader open it. Absent means the question was not asked. */
  access?: Record<string, boolean>;
  /** The reader's own name, so being addressed stands out from being mentioned. */
  me?: string | null;
  /** The names that reach somebody. Absent means the question was not asked. */
  people?: string[];
}) {
  return (
    <p className="note-text">
      {parseNote(body).map((token, index) => {
        if (token.kind === "text") return <span key={index}>{token.text}</span>;

        if (token.kind === "mention") {
          /*
           * A name nobody has is left as plain text.
           *
           * Only names reach people, so `@patxoga` and `@someone@gmail.com` ping
           * nobody. Drawing them as chips anyway would make a note look like it
           * reached somebody when it reached no one, which is the one
           * misunderstanding this panel must not create.
           */
          const known = !people || people.some((name) => name.toLowerCase() === token.handle.toLowerCase());
          if (!known) return <span key={index}>@{token.handle}</span>;

          const mine = me && token.handle.toLowerCase() === me.toLowerCase();
          return (
            <span key={index} className={`note-mention${mine ? " me" : ""}`}>
              @{token.handle}
            </span>
          );
        }

        if (token.tracking) return <TrackingChip key={index} token={token} />;

        const denied = access ? access[token.url] === false : false;
        return (
          <a
            key={index}
            className={`note-link${denied ? " denied" : ""}`}
            href={token.url}
            target="_blank"
            rel="noreferrer noopener"
            title={denied ? "This has not been shared with you" : token.url}
          >
            <LinkMark icon={token.icon} size={12} />
            <span>{token.label}</span>
            {denied ? <em>(no access)</em> : null}
          </a>
        );
      })}
    </p>
  );
}

/**
 * A tracking link, which copies and never navigates.
 *
 * Not an anchor at all: no href to middle-click, no context menu offering "open
 * in new tab". Loading one of these registers a click against the creator whose
 * earnings are usually on the same page, so the only safe way to look at one is
 * to not open it.
 */
function TrackingChip({ token }: { token: Extract<NoteToken, { kind: "link" }> }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <button
      type="button"
      className={`note-link tracking${copied ? " copied" : ""}`}
      title="A tracking link. Clicking copies it, because opening it would register a click."
      onClick={async () => {
        await copyText(token.url);
        setCopied(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), 1600);
      }}
    >
      {copied ? <CheckIcon size={12} /> : <LinkMark icon={token.icon} size={12} />}
      <span>{copied ? "Copied" : token.label}</span>
    </button>
  );
}
